import { Navigate, useLocation } from '@solidjs/router';
import { Show, type JSX } from 'solid-js';
import { isAuthenticated, isLoading, getUser } from '../store/session.store';
import { getBusiness, isBusinessLoaded } from '@/shared/stores/business.store';

interface AuthOnlyRouteProps {
  children: JSX.Element;
}

/**
 * Route guard for pages that require authentication but NOT a business.
 * Used for /no-business and /setup.
 *
 * - Not authenticated → redirect to /welcome
 * - Authenticated + already has a business → redirect to / (nothing to do here)
 * - Authenticated + no business → show the page
 */
export function AuthOnlyRoute(props: AuthOnlyRouteProps) {
  const location = useLocation();

  const hasBusiness = () => {
    // If the business store has been loaded (including force-refreshed after deletion),
    // trust it as the source of truth over the potentially stale JWT user object.
    if (isBusinessLoaded()) return !!getBusiness();
    // Store not loaded yet — use the JWT user's business field as an early hint
    // to avoid briefly flashing the no-business page during initial load.
    const u = getUser();
    return !!u?.business;
  };

  return (
    <Show
      when={!isLoading()}
      fallback={
        <div class="flex h-screen items-center justify-center bg-bg-app">
          <svg
            class="h-7 w-7 animate-spin text-text-muted"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="3"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>
      }
    >
      <Show
        when={isAuthenticated()}
        fallback={
          <Navigate
            href={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
          />
        }
      >
        {/* Already has a business — nothing to do on these pages */}
        <Show when={!hasBusiness()} fallback={<Navigate href="/" />}>
          {props.children}
        </Show>
      </Show>
    </Show>
  );
}
